import React, { useState, useEffect, useCallback } from 'react';
import { Users, BookOpen, Video, Download, Plus, Trash2, Crown, Shield, RefreshCw, ExternalLink } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const MemberManagement = () => {
  const [subTab, setSubTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [videos, setVideos] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [msg, setMsg] = useState('');

  // New content forms
  const [newArticle, setNewArticle] = useState({ title: '', excerpt: '', content: '', category: 'Premium', tags: '' });
  const [newVideo, setNewVideo] = useState({ title: '', description: '', youtube_url: '', category: 'Umum', duration: '' });
  const [newMaterial, setNewMaterial] = useState({ title: '', description: '', file_url: '', file_type: 'pdf', file_size: '', category: 'Umum' });
  const [creating, setCreating] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/member/admin/users`, { headers: getHeaders() });
      setUsers(res.data.users || []);
    } catch {}
  }, []);

  const fetchArticles = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/member/exclusive-articles`, { headers: getHeaders() });
      setArticles(res.data.articles || []);
    } catch {}
  }, []);

  const fetchVideos = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/member/learning-videos`, { headers: getHeaders() });
      setVideos(res.data.videos || []);
    } catch {}
  }, []);

  const fetchMaterials = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/member/materials`, { headers: getHeaders() });
      setMaterials(res.data.materials || []);
    } catch {}
  }, []);

  useEffect(() => {
    if (subTab === 'users') fetchUsers();
    if (subTab === 'exclusive') fetchArticles();
    if (subTab === 'videos') fetchVideos();
    if (subTab === 'downloads') fetchMaterials();
  }, [subTab, fetchUsers, fetchArticles, fetchVideos, fetchMaterials]);

  const toggleRole = async (userId, currentRole) => {
    const newRole = currentRole === 'admin' ? 'member' : 'admin';
    try {
      await axios.put(`${BACKEND_URL}/api/member/admin/users/${userId}/role`, { role: newRole }, { headers: getHeaders() });
      fetchUsers();
      setMsg(`Role berhasil diubah ke ${newRole}`);
    } catch (err) { setMsg(err.response?.data?.detail || 'Gagal update role'); }
  };

  const createArticle = async () => {
    if (!newArticle.title) return;
    setCreating(true);
    try {
      await axios.post(`${BACKEND_URL}/api/member/exclusive-articles`, {
        ...newArticle,
        tags: newArticle.tags.split(',').map(t => t.trim()).filter(Boolean),
        status: 'published',
      }, { headers: getHeaders() });
      setNewArticle({ title: '', excerpt: '', content: '', category: 'Premium', tags: '' });
      fetchArticles();
      setMsg('Artikel eksklusif berhasil dibuat!');
    } catch (err) { setMsg(err.response?.data?.detail || 'Gagal buat artikel'); }
    finally { setCreating(false); }
  };

  const createVideo = async () => {
    if (!newVideo.title || !newVideo.youtube_url) return;
    setCreating(true);
    try {
      await axios.post(`${BACKEND_URL}/api/member/learning-videos`, newVideo, { headers: getHeaders() });
      setNewVideo({ title: '', description: '', youtube_url: '', category: 'Umum', duration: '' });
      fetchVideos();
      setMsg('Video berhasil ditambahkan!');
    } catch (err) { setMsg(err.response?.data?.detail || 'Gagal tambah video'); }
    finally { setCreating(false); }
  };

  const createMaterial = async () => {
    if (!newMaterial.title) return;
    setCreating(true);
    try {
      await axios.post(`${BACKEND_URL}/api/member/materials`, newMaterial, { headers: getHeaders() });
      setNewMaterial({ title: '', description: '', file_url: '', file_type: 'pdf', file_size: '', category: 'Umum' });
      fetchMaterials();
      setMsg('Materi berhasil ditambahkan!');
    } catch (err) { setMsg(err.response?.data?.detail || 'Gagal tambah materi'); }
    finally { setCreating(false); }
  };

  const deleteItem = async (type, id) => {
    const url = type === 'article' ? `exclusive-articles/${id}` : type === 'video' ? `learning-videos/${id}` : `materials/${id}`;
    try {
      await axios.delete(`${BACKEND_URL}/api/member/${url}`, { headers: getHeaders() });
      if (type === 'article') fetchArticles();
      if (type === 'video') fetchVideos();
      if (type === 'material') fetchMaterials();
      setMsg('Berhasil dihapus');
    } catch (err) { setMsg(err.response?.data?.detail || 'Gagal hapus'); }
  };

  const inputStyle = { width: '100%', padding: '0.5rem 0.75rem', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0.375rem', color: '#EDEDED', fontSize: '0.85rem' };

  const subTabs = [
    { id: 'users', label: 'Daftar User', icon: <Users size={14} /> },
    { id: 'exclusive', label: 'Artikel Eksklusif', icon: <BookOpen size={14} /> },
    { id: 'videos', label: 'Video', icon: <Video size={14} /> },
    { id: 'downloads', label: 'Materi Download', icon: <Download size={14} /> },
  ];

  return (
    <div data-testid="member-management">
      <h2 style={{ fontSize: '1.2rem', fontWeight: 600, marginBottom: '1rem', color: '#EDEDED' }}>
        <Crown size={20} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.5rem', color: '#d9fb06' }} />
        Member Management
      </h2>

      {msg && <p style={{ color: msg.includes('berhasil') ? '#4ade80' : '#f87171', fontSize: '0.85rem', marginBottom: '1rem' }} data-testid="member-mgmt-msg">{msg}</p>}

      {/* Sub tabs */}
      <div style={{ display: 'flex', gap: '0.375rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        {subTabs.map(st => (
          <button
            key={st.id}
            onClick={() => { setSubTab(st.id); setMsg(''); }}
            data-testid={`member-subtab-${st.id}`}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.375rem',
              padding: '0.5rem 0.875rem', borderRadius: '0.375rem', border: 'none', cursor: 'pointer',
              fontSize: '0.8rem', fontWeight: subTab === st.id ? 600 : 400,
              background: subTab === st.id ? 'rgba(217,251,6,0.1)' : 'rgba(255,255,255,0.04)',
              color: subTab === st.id ? '#d9fb06' : '#A1A1AA',
            }}
          >
            {st.icon} {st.label}
          </button>
        ))}
      </div>

      {/* Users */}
      {subTab === 'users' && (
        <div>
          <p style={{ fontSize: '0.8rem', color: '#71717A', marginBottom: '0.75rem' }}>{users.length} pengguna terdaftar</p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  <th style={{ textAlign: 'left', padding: '0.5rem', fontSize: '0.75rem', color: '#71717A' }}>Nama</th>
                  <th style={{ textAlign: 'left', padding: '0.5rem', fontSize: '0.75rem', color: '#71717A' }}>Email</th>
                  <th style={{ textAlign: 'center', padding: '0.5rem', fontSize: '0.75rem', color: '#71717A' }}>Role</th>
                  <th style={{ textAlign: 'center', padding: '0.5rem', fontSize: '0.75rem', color: '#71717A' }}>Auth</th>
                  <th style={{ textAlign: 'left', padding: '0.5rem', fontSize: '0.75rem', color: '#71717A' }}>Terdaftar</th>
                  <th style={{ textAlign: 'center', padding: '0.5rem', fontSize: '0.75rem', color: '#71717A' }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.user_id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <td style={{ padding: '0.5rem', fontSize: '0.85rem', color: '#EDEDED' }}>{u.name || '-'}</td>
                    <td style={{ padding: '0.5rem', fontSize: '0.8rem', color: '#A1A1AA' }}>{u.email}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: '8px', fontSize: '0.7rem', fontWeight: 600,
                        background: u.role === 'admin' ? 'rgba(248,113,113,0.15)' : 'rgba(74,222,128,0.15)',
                        color: u.role === 'admin' ? '#f87171' : '#4ade80',
                      }}>
                        {u.role}
                      </span>
                    </td>
                    <td style={{ padding: '0.5rem', textAlign: 'center', fontSize: '0.75rem', color: '#71717A' }}>
                      {u.auth_method === 'google' ? 'Google' : 'Email'}
                    </td>
                    <td style={{ padding: '0.5rem', fontSize: '0.75rem', color: '#71717A' }}>
                      {u.created_at ? new Date(u.created_at).toLocaleDateString('id-ID') : '-'}
                    </td>
                    <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                      <button onClick={() => toggleRole(u.user_id, u.role)} style={{
                        padding: '2px 8px', borderRadius: '4px', border: 'none', cursor: 'pointer',
                        fontSize: '0.7rem', background: 'rgba(255,255,255,0.08)', color: '#A1A1AA',
                      }}>
                        {u.role === 'admin' ? 'Jadikan Member' : 'Jadikan Admin'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Exclusive Articles */}
      {subTab === 'exclusive' && (
        <div>
          {/* Create form */}
          <div className="stat-card" style={{ marginBottom: '1rem' }}>
            <h4 style={{ fontSize: '0.9rem', marginBottom: '0.75rem', color: '#EDEDED' }}><Plus size={14} style={{ display: 'inline', verticalAlign: 'middle' }} /> Buat Artikel Eksklusif</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <input value={newArticle.title} onChange={e => setNewArticle({...newArticle, title: e.target.value})} placeholder="Judul artikel" style={inputStyle} data-testid="exclusive-title" />
              <input value={newArticle.excerpt} onChange={e => setNewArticle({...newArticle, excerpt: e.target.value})} placeholder="Ringkasan singkat" style={inputStyle} />
              <textarea value={newArticle.content} onChange={e => setNewArticle({...newArticle, content: e.target.value})} placeholder="Konten HTML artikel" rows={4} style={{...inputStyle, resize: 'vertical'}} data-testid="exclusive-content" />
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input value={newArticle.category} onChange={e => setNewArticle({...newArticle, category: e.target.value})} placeholder="Kategori" style={{...inputStyle, flex: 1}} />
                <input value={newArticle.tags} onChange={e => setNewArticle({...newArticle, tags: e.target.value})} placeholder="Tags (koma)" style={{...inputStyle, flex: 2}} />
              </div>
              <button onClick={createArticle} disabled={creating || !newArticle.title} className="btn-primary" style={{ alignSelf: 'flex-start', padding: '0.5rem 1.25rem' }} data-testid="create-exclusive-btn">
                <Plus size={14} /> {creating ? 'Menyimpan...' : 'Simpan Artikel'}
              </button>
            </div>
          </div>

          {/* List */}
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            {articles.map(a => (
              <div key={a.id} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '0.5rem', padding: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h4 style={{ fontSize: '0.9rem', fontWeight: 600, color: '#EDEDED' }}>{a.title}</h4>
                  <p style={{ fontSize: '0.75rem', color: '#71717A' }}>{a.category} | {a.views || 0} views | {a.created_at ? new Date(a.created_at).toLocaleDateString('id-ID') : ''}</p>
                </div>
                <button onClick={() => deleteItem('article', a.id)} style={{ background: 'rgba(248,113,113,0.15)', border: 'none', color: '#f87171', cursor: 'pointer', padding: '0.375rem', borderRadius: '0.25rem' }}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Videos */}
      {subTab === 'videos' && (
        <div>
          <div className="stat-card" style={{ marginBottom: '1rem' }}>
            <h4 style={{ fontSize: '0.9rem', marginBottom: '0.75rem', color: '#EDEDED' }}><Plus size={14} style={{ display: 'inline', verticalAlign: 'middle' }} /> Tambah Video Pembelajaran</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <input value={newVideo.title} onChange={e => setNewVideo({...newVideo, title: e.target.value})} placeholder="Judul video" style={inputStyle} data-testid="video-title" />
              <input value={newVideo.youtube_url} onChange={e => setNewVideo({...newVideo, youtube_url: e.target.value})} placeholder="URL YouTube (e.g., https://youtube.com/watch?v=...)" style={inputStyle} data-testid="video-url" />
              <input value={newVideo.description} onChange={e => setNewVideo({...newVideo, description: e.target.value})} placeholder="Deskripsi" style={inputStyle} />
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input value={newVideo.category} onChange={e => setNewVideo({...newVideo, category: e.target.value})} placeholder="Kategori" style={{...inputStyle, flex: 1}} />
                <input value={newVideo.duration} onChange={e => setNewVideo({...newVideo, duration: e.target.value})} placeholder="Durasi (e.g., 15:30)" style={{...inputStyle, flex: 1}} />
              </div>
              <button onClick={createVideo} disabled={creating || !newVideo.title || !newVideo.youtube_url} className="btn-primary" style={{ alignSelf: 'flex-start', padding: '0.5rem 1.25rem' }} data-testid="create-video-btn">
                <Plus size={14} /> {creating ? 'Menyimpan...' : 'Tambah Video'}
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gap: '0.5rem' }}>
            {videos.map(v => (
              <div key={v.id} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '0.5rem', padding: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <img src={`https://img.youtube.com/vi/${v.youtube_id}/default.jpg`} alt="" style={{ width: '60px', borderRadius: '4px' }} />
                  <div>
                    <h4 style={{ fontSize: '0.9rem', fontWeight: 600, color: '#EDEDED' }}>{v.title}</h4>
                    <p style={{ fontSize: '0.75rem', color: '#71717A' }}>{v.category} | {v.duration}</p>
                  </div>
                </div>
                <button onClick={() => deleteItem('video', v.id)} style={{ background: 'rgba(248,113,113,0.15)', border: 'none', color: '#f87171', cursor: 'pointer', padding: '0.375rem', borderRadius: '0.25rem' }}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Materials */}
      {subTab === 'downloads' && (
        <div>
          <div className="stat-card" style={{ marginBottom: '1rem' }}>
            <h4 style={{ fontSize: '0.9rem', marginBottom: '0.75rem', color: '#EDEDED' }}><Plus size={14} style={{ display: 'inline', verticalAlign: 'middle' }} /> Tambah Materi Download</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <input value={newMaterial.title} onChange={e => setNewMaterial({...newMaterial, title: e.target.value})} placeholder="Judul materi" style={inputStyle} data-testid="material-title" />
              <input value={newMaterial.description} onChange={e => setNewMaterial({...newMaterial, description: e.target.value})} placeholder="Deskripsi" style={inputStyle} />
              <input value={newMaterial.file_url} onChange={e => setNewMaterial({...newMaterial, file_url: e.target.value})} placeholder="URL file (Google Drive, Dropbox, dll)" style={inputStyle} data-testid="material-url" />
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <select value={newMaterial.file_type} onChange={e => setNewMaterial({...newMaterial, file_type: e.target.value})} style={{...inputStyle, flex: 1}}>
                  <option value="pdf">PDF</option>
                  <option value="xlsx">Excel</option>
                  <option value="docx">Word</option>
                  <option value="pptx">PowerPoint</option>
                  <option value="zip">ZIP</option>
                </select>
                <input value={newMaterial.file_size} onChange={e => setNewMaterial({...newMaterial, file_size: e.target.value})} placeholder="Ukuran (e.g., 2.4 MB)" style={{...inputStyle, flex: 1}} />
                <input value={newMaterial.category} onChange={e => setNewMaterial({...newMaterial, category: e.target.value})} placeholder="Kategori" style={{...inputStyle, flex: 1}} />
              </div>
              <button onClick={createMaterial} disabled={creating || !newMaterial.title} className="btn-primary" style={{ alignSelf: 'flex-start', padding: '0.5rem 1.25rem' }} data-testid="create-material-btn">
                <Plus size={14} /> {creating ? 'Menyimpan...' : 'Tambah Materi'}
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gap: '0.5rem' }}>
            {materials.map(m => (
              <div key={m.id} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '0.5rem', padding: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h4 style={{ fontSize: '0.9rem', fontWeight: 600, color: '#EDEDED' }}>{m.title}</h4>
                  <p style={{ fontSize: '0.75rem', color: '#71717A' }}>{m.file_type?.toUpperCase()} | {m.file_size} | {m.download_count || 0} downloads</p>
                </div>
                <button onClick={() => deleteItem('material', m.id)} style={{ background: 'rgba(248,113,113,0.15)', border: 'none', color: '#f87171', cursor: 'pointer', padding: '0.375rem', borderRadius: '0.25rem' }}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberManagement;
